import React, { useState, useRef, useEffect } from 'react';

/**
 * OptimizedImage - A lazy-loading image component with placeholder support
 * Uses Intersection Observer for efficient loading
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  style = {},
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMGIwMDRjIi8+PC9zdmc+',
  onLoad,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(placeholder);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && src) {
      // Preload the image
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setCurrentSrc(src);
        setIsLoaded(true);
        if (onLoad) onLoad();
      };
    }
  }, [isInView, src, onLoad]);

  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-70'} ${className}`}
      style={style}
      loading="lazy"
      decoding="async"
      {...props}
    />
  );
};

/**
 * OptimizedVideo - A lazy-loading video component with preload support
 */
export const OptimizedVideo = ({ 
  src, 
  poster,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  preload = 'metadata',
  onLoadedData,
  ...props 
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Start loading videos earlier
        threshold: 0.01
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoadedData = () => {
    setIsLoaded(true);
    if (onLoadedData) onLoadedData();
  };

  return (
    <video
      ref={videoRef}
      src={isInView ? src : undefined}
      poster={poster}
      className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-80'} ${className}`}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      preload={isInView ? preload : 'none'}
      onLoadedData={handleLoadedData}
      {...props}
    />
  );
};

/**
 * Preload critical assets (hero images/videos)
 */
export const preloadAssets = (assets) => {
  assets.forEach(asset => {
    if (asset.type === 'image') {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = asset.src;
      document.head.appendChild(link);
    } else if (asset.type === 'video') {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = asset.src;
      document.head.appendChild(link);
    }
  });
};

/**
 * Cache image in browser
 */
export const cacheImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Cache multiple images
 */
export const cacheImages = async (sources) => {
  const promises = sources.map(src => cacheImage(src));
  return Promise.allSettled(promises);
};

export default OptimizedImage;
